import { isInternalRoleName } from 'constants/roles'
import { useCanAccess } from './useCanAccess'
import { useWorkOSAuth } from './useWorkOSAuth'
import { useHomeChecklist } from './useHomeChecklist'
import { useUserPreferencesQuery } from './api/useUserPreferences'

/**
 * Whether the first-run setup CHECKLIST is still live for this user (VRG-338 / VRG-340).
 *
 * ⚠️ THIS USED TO ANSWER "is HOME still live", and the rename is the whole point of the
 * change. Home is now PERMANENT — `/` always renders it, and its sidebar entry never
 * disappears. What retires is the setup-checklist BLOCK.
 *
 * Why the earlier model was wrong: "a permanent Home is dead weight once onboarding is done"
 * was true when Home held only a checklist and tours, both of which ARE scaffolding. The page
 * has since gained orientation cards, account shortcuts and an activity feed, none of which
 * are. The premise that justified retiring the page stopped being true, so the page stays and
 * the scaffolding inside it goes — which also kills two smaller problems: `/` is no longer a
 * redirect for every established brand, and a sidebar entry the user has learned no longer
 * vanishes as a reward for finishing setup.
 *
 * The checklist retires on EITHER of two signals, and the asymmetry is the point:
 *
 *   - the company setup checklist completing — COMPANY-scoped, retires for everyone at once
 *   - this user explicitly dismissing it     — USER-scoped, stored in `users.preferences`
 *
 * So an admin who dismisses Home does not hide a new hire's getting-started block. Dismissal
 * is server-side rather than localStorage because it should follow the person across devices
 * — the sidebar's display mode is localStorage by deliberate exception (see `displayMode.ts`,
 * which contrasts itself with theme and tour progress for exactly this reason).
 *
 * There is no "has onboarded" flag anywhere and there must not be one: the checklist is
 * derived from real state, so its completion IS the signal.
 *
 * Returns `false` while preferences are still loading, so the block never flashes in and out
 * for someone who dismissed it months ago.
 */
export const useChecklistVisibility = () => {
  const { user } = useWorkOSAuth()
  const { preferences, isLoading } = useUserPreferencesQuery()

  // Internal staff never see the brand setup checklist; `/` bounces them to the internal
  // console (HomePage owns that redirect).
  // VRG-410 — the shared predicate, read off the RAW WorkOS role. `profile.role.name` is
  // the mapped name, which falls back to `'company_team_member'` for `vera_fulfillment` /
  // `vera_finance`, so those two would have been offered a brand setup checklist.
  const isInternalUser = isInternalRoleName(user?.role?.name)

  // Legacy `homeDismissed` folds into collapsed — see the Preferences docblock.
  const isCollapsed =
    preferences.homeChecklistCollapsed === true || preferences.homeDismissed === true

  // `action`, not `role`: role checks return false for a Vera staffer inside a company space
  // (useCanAccess.ts:22-33), which would wrongly treat them as a non-admin here.
  const { isHasAccess: canManageCompany } = useCanAccess({ action: 'manage_company' })

  // The COMPANY-scoped half. Only meaningful for someone who can act on setup: a Design hire
  // has no checklist, so completion cannot be what retires Home for them — dismissal, or
  // simply never returning, is. Querying it for them would also fire six requests to compute
  // a value that is never read.
  const checklist = useHomeChecklist({ enabled: !isInternalUser && canManageCompany })

  /*
    RETIRES ON COMPLETE **AND CREDITED**, not on complete alone.

    Claiming each block's free activation is a BUTTON on the card now, rather than an effect
    that fired on the completing transition. That change has a consequence one level up from
    the card, and missing it would have reintroduced the exact defect it fixes: the final
    block becomes claimable at the same instant `isComplete` goes true, and retiring the
    checklist on `isComplete` unmounts the card — and the button — in that same render. The
    third activation would have been structurally unclaimable, forever, for every company.

    Same principle the card applies to its own accordion (`SetupChecklist`'s `firstIncomplete`):
    a block with an unclaimed reward still has an action in it, so it is not finished. Here
    the unit is the whole checklist rather than one block.

    `blocks` is `[]` while loading, so `every` is vacuously true — harmless, because
    `isComplete` is already false until the query settles.
  */
  const allRewardsClaimed = checklist.blocks.every((b) => b.earned)
  const checklistComplete = canManageCompany && checklist.isComplete && allRewardsClaimed

  /*
    COLLAPSED IS NOT HIDDEN. `isChecklistLive` means "there is still setup outstanding", which
    is a fact about the company, not about whether this user wants to look at it. Collapsing
    changes how the block RENDERS, not whether it exists — which is the whole point of
    replacing dismissal: nothing is destroyed, so nothing needs restoring.
  */
  const isChecklistLive = !isLoading && !isInternalUser && !checklistComplete

  // `checklist` is returned as well as consumed. The Sidebar already calls this hook on every
  // page, so the six queries are running app-wide regardless — handing the derived counts back
  // lets the sidebar badge and the progress toast read them for free rather than mounting a
  // second copy of the same six requests.
  return { isChecklistLive, isCollapsed, isLoading: isLoading || checklist.isLoading, checklist }
}
